import React, {
	useRef,
	useState,
	useEffect,
	useCallback,
	useContext,
} from "react";
import { sample } from "lodash";
import {
	Animated,
	LayoutChangeEvent,
	LayoutRectangle,
	View,
} from "react-native";
import { useIsFocused } from "@react-navigation/native";
import Emoji, { EmojiType } from "myplan/components/Emoji";
import Quote from "myplan/components/Quote";
import { useQuotes } from "myplan/utils/contentful";
import { useMe } from "myplan/utils/me";
import {
	Container,
	Prompt,
	Response,
	PromptTitle,
	Selector,
	EMOJI_TRANSLATE_X_MARGIN,
	ResponseTitle,
	RedoContainer,
} from "./styles";
import Redo from "../Redo";
import { FurnitureContext } from "../../utils/furniture";

const emojiKeys = ["happy", "neutral", "overwhelmed", "sad", "angry"];
const QUOTE_TRANSLATE_Y_ORIGIN = -40;

type MoodSelectorProps = {
	contentTranslateY: Animated.Value;
};

const createEmojiMap = () => {
	return emojiKeys.reduce((acc, key) => {
		const emoji: any = {
			// @ts-ignore
			translate: new Animated.ValueXY(0, 0),
			opacity: new Animated.Value(1),
			frame: null,
		};

		emoji.onLayout = (event: LayoutChangeEvent) => {
			emoji.frame = event.nativeEvent.layout;
		};

		acc[key] = emoji;

		return acc;
	}, {});
};

const calculateEmojiTranslate = (
	emojiFrame,
	selectorFrame,
	responseTitleFrame
) => {
	return {
		x:
			responseTitleFrame.x +
			responseTitleFrame.width -
			emojiFrame.x +
			EMOJI_TRANSLATE_X_MARGIN,
		y:
			responseTitleFrame.y -
			selectorFrame.y -
			(selectorFrame.height - responseTitleFrame.height) / 2,
	};
};

const MoodSelector = ({ contentTranslateY }: MoodSelectorProps) => {
	const me = useMe();
	const isFocused = useIsFocused();
	const furniture = useContext(FurnitureContext);
	const [quotes] = useQuotes();
	const isLoading = !furniture || quotes.isLoading;

	const [containerFrame, setContainerFrame] = useState<LayoutRectangle>();
	const [responseTitleFrame, setResponseTitleFrame] =
		useState<LayoutRectangle | null>(null);
	const [selectorFrame, setSelectorFrame] = useState<LayoutRectangle>();
	const [isResponseActive, setIsResponseActive] = useState(false);

	const promptOpacity = useRef(new Animated.Value(1)).current;
	const responseOpacity = useRef(new Animated.Value(0)).current;
	const quoteOpacity = useRef(new Animated.Value(0)).current;
	const quoteTranslateY = useRef(
		quoteOpacity.interpolate({
			inputRange: [0, 1],
			outputRange: [QUOTE_TRANSLATE_Y_ORIGIN, 0],
		})
	).current;

	const emojiMap = useRef(createEmojiMap()).current;
	const [activeEmojiKey, setActiveEmojiKey] = useState<EmojiType>();
	const [activeQuote, setActiveQuote] = useState(null);

	/**
	 * This is a fix for Android, to resolve a very specific bug, wherein
	 * React Native on Android doesn't always process native-driven animations
	 * correctly on the initial render. This snippet solves that by forcing an
	 * initial processing of the Animated value. This bug only effects RN on Android.
	 * If we see this stop occurring on Android, we should remove this effect.
	 */
	useEffect(() => {
		// @ts-ignore
		const initialValue = promptOpacity._value;

		Animated.sequence([
			Animated.timing(promptOpacity, {
				toValue: initialValue - 0.00001,
				duration: 0,
				useNativeDriver: true,
			}),
			Animated.timing(promptOpacity, {
				toValue: initialValue,
				duration: 0,
				useNativeDriver: true,
			}),
		]).start();
	}, []);

	useEffect(() => {
		if (!isFocused) goToPrompt();
	}, [isFocused]);

	useEffect(() => {
		if (activeEmojiKey && quotes.data) {
			const moodQuotes = quotes.data.filter((quote) => {
				return quote.moods.find((mood) => mood.key === activeEmojiKey);
			});
			const _activeQuote = sample(moodQuotes);

			setActiveQuote(_activeQuote);
		}
	}, [quotes?.data, activeEmojiKey]);

	const onContainerLayout = useCallback((event: LayoutChangeEvent) => {
		setContainerFrame(event.nativeEvent.layout);
	}, []);

	const onResponseLayout = useCallback(
		(event: LayoutChangeEvent) => {
			if (activeEmojiKey) {
				goToResponse(activeEmojiKey, event.nativeEvent.layout);
			}
		},
		[activeEmojiKey]
	);

	const onResponseTitleLayout = useCallback((event: LayoutChangeEvent) => {
		setResponseTitleFrame(event.nativeEvent.layout);
	}, []);

	const onSelectorLayout = useCallback((event: LayoutChangeEvent) => {
		setSelectorFrame(event.nativeEvent.layout);
	}, []);

	const goToResponse = (key: EmojiType, responseFrame: LayoutRectangle) => {
		const QUOTE_MARGIN_BOTTOM = 35;

		if (!isResponseActive) {
			const activeEmoji = emojiMap[key];
			const inactiveEmojiOpacity = Object.keys(emojiMap).reduce(
				(acc, _key) => {
					if (_key !== key) {
						const emoji = emojiMap[_key];
						const opacity = Animated.timing(emoji.opacity, {
							toValue: 0,
							duration: 400,
							useNativeDriver: true,
						});

						acc.push(opacity);
					}

					return acc;
				},
				[]
			);

			Animated.sequence([
				Animated.parallel([
					Animated.timing(promptOpacity, {
						toValue: 0,
						duration: 400,
						useNativeDriver: true,
					}),
					...inactiveEmojiOpacity,
				]),
				Animated.timing(activeEmoji.translate, {
					toValue: calculateEmojiTranslate(
						activeEmoji.frame,
						selectorFrame,
						responseTitleFrame
					),
					duration: 400,
					useNativeDriver: true,
				}),
				Animated.timing(responseOpacity, {
					toValue: 1,
					duration: 400,
					useNativeDriver: true,
				}),
				Animated.parallel([
					Animated.timing(contentTranslateY, {
						toValue:
							responseFrame.height -
							containerFrame.height +
							QUOTE_MARGIN_BOTTOM,
						duration: 400,
						useNativeDriver: false,
					}),
					Animated.timing(quoteOpacity, {
						toValue: 1,
						duration: 400,
						useNativeDriver: true,
					}),
				]),
			]).start(() => setIsResponseActive(true));
		}
	};

	const goToPrompt = () => {
		const key = activeEmojiKey;

		if (isResponseActive) {
			const activeEmoji = emojiMap[key];
			const inactiveEmojiOpacity = Object.keys(emojiMap).reduce(
				(acc, _key) => {
					if (_key !== key) {
						const emoji = emojiMap[_key];
						const opacity = Animated.timing(emoji.opacity, {
							toValue: 1,
							duration: 400,
							useNativeDriver: true,
						});

						acc.push(opacity);
					}

					return acc;
				},
				[]
			);

			setActiveEmojiKey(null);

			Animated.sequence([
				Animated.parallel([
					Animated.timing(contentTranslateY, {
						toValue: 0,
						duration: 400,
						useNativeDriver: false,
					}),
					Animated.timing(quoteOpacity, {
						toValue: 0,
						duration: 400,
						useNativeDriver: true,
					}),
				]),
				Animated.timing(responseOpacity, {
					toValue: 0,
					duration: 400,
					useNativeDriver: true,
				}),
				Animated.timing(activeEmoji.translate, {
					toValue: {
						x: 0,
						y: 0,
					},
					duration: 400,
					useNativeDriver: true,
				}),
				Animated.parallel([
					Animated.timing(promptOpacity, {
						toValue: 1,
						duration: 400,
						useNativeDriver: true,
					}),
					...inactiveEmojiOpacity,
				]),
			]).start(() => {
				setActiveQuote(null);
				setIsResponseActive(false);
			});
		}
	};

	if (isLoading) {
		return <Container />;
	}

	return (
		<Container onLayout={onContainerLayout}>
			<View onLayout={onContainerLayout}>
				<Prompt style={{ opacity: promptOpacity }}>
					<PromptTitle type="h3">{furniture.moodPrompt}</PromptTitle>
				</Prompt>
				<Selector onLayout={onSelectorLayout}>
					{Object.keys(emojiMap).map((key: EmojiType) => {
						const emoji = emojiMap[key];
						const style: any = {
							transform: emoji.translate.getTranslateTransform(),
							opacity: emoji.opacity,
						};

						return (
							<Animated.View
								key={key}
								style={style}
								onLayout={emoji.onLayout}>
								<Emoji
									type={key}
									isActive={key === activeEmojiKey}
									onPress={() => {
										if (!activeEmojiKey) {
											me.api.recordMood(key);
											setActiveEmojiKey(key);
										}
									}}
								/>
							</Animated.View>
						);
					})}
				</Selector>
				<Response
					onLayout={onResponseLayout}
					style={{ opacity: responseOpacity }}>
					<RedoContainer responseTitleFrame={responseTitleFrame}>
						<Redo
							label={furniture.moodRedo}
							onPress={() => {
								me.api.redoMood();
								goToPrompt();
							}}
						/>
					</RedoContainer>
					<ResponseTitle type="h3" onLayout={onResponseTitleLayout}>
						You're feeling
					</ResponseTitle>
					<Quote
						opacity={quoteOpacity}
						translateY={quoteTranslateY}
						body={activeQuote?.body}
						author={activeQuote?.author}
					/>
				</Response>
			</View>
		</Container>
	);
};

export default MoodSelector;
